import { NumberType, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import type { ExternalRuleFormat, PhoneCountryCode, RuleFormat, ValidationFunc, ValidationRules } from './types';

const isCorrectPhoneNumberType = (phoneNumberType: NumberType, locale: string) => {
    if (locale === 'DK') {
        // In DK, landline and mobile numbers are mixed up.
        // FIXED_LINE_OR_MOBILE doesn't cover both MOBILE and FIXED_LINE, it means libphonenumber can't say exactly.
        // @see: https://en.wikipedia.org/wiki/Telephone_numbers_in_Denmark
        return (
            phoneNumberType === 'FIXED_LINE_OR_MOBILE' ||
            phoneNumberType === 'MOBILE' ||
            phoneNumberType === 'FIXED_LINE'
        );
    }
    return phoneNumberType === 'MOBILE';
};


const createValidationMethod = (rule: RuleFormat | ExternalRuleFormat) => {
    switch (rule.type) {
        case 'MAX_LENGTH':
            return {
                longerThanOrEqualTo: ((value: string) =>
                    value.length <= Number(rule.value) || rule.message) as ValidationFunc,
            };
        case 'MIN_LENGTH':
            return {
                shorterThanOrEqualTo: ((value: string) =>
                    value.length >= Number(rule.value) || rule.message) as ValidationFunc,
            };
        case 'LENGTH':
            return {
                lengthEqualTo: ((value: string) =>
                    value.length === Number(rule.value) || rule.message) as ValidationFunc,
            };
        case 'MAX_VALUE':
            return {
                lessThanOrEqualTo: ((value: number) =>
                    value <= Number(rule.value) || rule.message) as ValidationFunc,
            };
        case 'MIN_VALUE':
            return {
                largerThanOrEqualTo: ((value: number) =>
                    value >= Number(rule.value) || rule.message) as ValidationFunc,
            };
        case 'ALLOWED_CHARACTERS_REGEXP':
            return {
                allowedCharacters: ((value: string) =>
                    new RegExp(rule?.value ?? '').test(value) || rule.message) as ValidationFunc,
            };
        case 'MOBILE_PHONE_NUMBER':
            return {
                validPhoneNumber: ((value: string) => {
                    if (value.length === 0) return rule.message;

                    try {
                        const countryCode: PhoneCountryCode = rule.value as PhoneCountryCode; // NO, FI, DK or SE
                        const phoneNumber = parsePhoneNumberFromString(value, countryCode);

                        if (!phoneNumber?.isValid()) {
                            return rule.message;
                        }

                        const phoneNumberType = phoneNumber.getType();

                        if (!isCorrectPhoneNumberType(phoneNumberType, countryCode)) {
                            return rule.message;
                        }

                        return true;
                    } catch (err) {
                        // libphonenumber throws if it can't parse the value as phone number.
                        // Treat it as invalid value to avoid uncaught exceptions.
                        return rule.message;
                    }
                }) as ValidationFunc,
            };
        case 'REQUIRED':
            return {
                valueIsRequired: ((value: string) =>
                    !!value.length || rule.message) as ValidationFunc,
            };

        default:
            console.error('Received a validation rule that is not handled.');
            return {};
    }
};

const ruleGenerator = (rules: ValidationRules['rules']) =>
    rules.reduce(
        (prevValue, currentValue) => ({ ...prevValue, ...createValidationMethod(currentValue) }),
        {},
    ) as { [s: string]: ValidationFunc };

const validate = (validationRules: ValidationRules, value: string) => {
    // Get a list of all validation rules
    const validationFuncs = validationRules && Object.values(ruleGenerator(validationRules.rules));
    // If all validation rules return true, the value is valid
    if (value && validationFuncs?.every((rule) => rule(value) === true)) {
        return true;
    }
    // If all validation functions are not true, find the first validation rule that returns an error message
    const getValidationMessage =
        validationFuncs && validationFuncs.find((rule) => typeof rule(value) === 'string');
    return (getValidationMessage && getValidationMessage(value)) || '';
};

export const isValid = (validationRules: ValidationRules, value: string) =>
    validate(validationRules, value) === true;

export const getErrorMessage = (validationRules: ValidationRules, value: string) => {
    const result = validate(validationRules, value);
    if (typeof result === 'string') {
        return result;
    }
    return undefined;
};
export default ruleGenerator;
